import { useEffect, type CSSProperties } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import HomeScreen from './screens/HomeScreen.js';
import CalculatorScreen from './screens/CalculatorScreen.js';
import SettingsScreen from './screens/SettingsScreen.js';
import ShareReceiverScreen from './screens/ShareReceiverScreen.js';
import OnboardingScreen from './screens/OnboardingScreen.js';
import LevelScreen from './screens/LevelScreen.js';
import { CALCULATORS, type CalculatorConfig } from './screens/calculator-configs.js';
import { PaywallSheet } from './components/PaywallSheet.js';
import { useAppSettings } from './lib/app-settings.js';
import { useNeedsOnboarding } from './lib/onboarding.js';
import { sharePathFromHash } from './lib/share.js';
import { useSubscription } from './lib/subscription.js';

export default function App() {
  const location = useLocation();
  const { darkMode, highContrastText } = useAppSettings();
  const needsFirstRunSetup = useNeedsOnboarding();
  const isCalculatorRoute = CALCULATORS.some((cfg) => location.pathname === `/${cfg.id}`);
  const isLevelRoute = location.pathname === '/level';
  const isSquareAlignRoute = location.pathname === '/square-align';
  const canReturnAfterOnboarding =
    location.pathname === '/' ||
    location.pathname === '/settings' ||
    isLevelRoute ||
    isSquareAlignRoute ||
    isCalculatorRoute;
  const isImmersiveRoute = isCalculatorRoute || isLevelRoute || isSquareAlignRoute;
  const isOnboardingRoute = location.pathname === '/onboarding';
  const legacySharePath = sharePathFromHash(location.hash);
  const isShareRoute = location.pathname.startsWith('/share/') || legacySharePath !== null;
  const showBackLink = location.pathname !== '/' && location.pathname !== '/settings';
  const shouldGateOnboarding = needsFirstRunSetup && !isOnboardingRoute && !isShareRoute;
  const onboardingReturnPath = `${location.pathname}${location.search}`;

  useEffect(() => {
    const theme = darkMode ? 'dark' : 'light';
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.dataset.contrast = highContrastText ? 'high' : 'normal';
  }, [highContrastText]);

  if (legacySharePath) {
    return <Navigate to={legacySharePath} replace />;
  }

  return (
    <div className="app">
      {!isImmersiveRoute && !isOnboardingRoute && (
        <header className="app-header">
          <Link to="/" className="app-header-title" style={{ textDecoration: 'none' }}>
            True<span className="accent">bend</span>
          </Link>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {!showBackLink ? (
              <>
                <TopNavLink to="/" label="Bends" exact />
                <SettingsNavLink />
              </>
            ) : (
              <Link to="/" className="back-link" aria-label="Back to all bends">
                <BackArrowIcon />
                <span>All bends</span>
              </Link>
            )}
          </nav>
        </header>
      )}
      <main
        className="app-main"
        style={
          isImmersiveRoute ? { padding: 'var(--space-4)' } : isOnboardingRoute ? { padding: 0 } : {}
        }
      >
        <Routes>
          <Route path="/onboarding" element={<OnboardingScreen />} />
          {shouldGateOnboarding ? (
            <Route
              path="*"
              element={
                <Navigate
                  to="/onboarding"
                  replace
                  state={{ returnTo: canReturnAfterOnboarding ? onboardingReturnPath : '/' }}
                />
              }
            />
          ) : (
            <>
              <Route path="/" element={<HomeScreen />} />
              <Route path="/level" element={<LevelRoute />} />
              <Route path="/square-align" element={<SquareAlignRoute />} />
              <Route path="/settings" element={<SettingsScreen />} />
              <Route path="/share/:token" element={<ShareReceiverScreen />} />
              {CALCULATORS.map((cfg) => (
                <Route
                  key={cfg.id}
                  path={`/${cfg.id}`}
                  element={<CalculatorRoute config={cfg} />}
                />
              ))}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </main>
    </div>
  );
}

function CalculatorRoute({ config }: { config: CalculatorConfig }) {
  const navigate = useNavigate();
  const { isPro } = useSubscription();

  if (!config.requiresPro || isPro) {
    return <CalculatorScreen config={config} />;
  }

  return (
    <div style={lockedRouteStyle}>
      <LockedBackLink returnPath="/" />
      <section aria-label="Truebend Pro calculator locked" style={lockedHeroStyle}>
        <div style={lockedEyebrowStyle}>Truebend Pro</div>
        <h1 style={lockedTitleStyle}>{config.displayName}</h1>
        <p style={lockedCopyStyle}>
          Upgrade to unlock this calculator and the rest of the advanced bend library.
        </p>
      </section>
      <PaywallSheet
        feature={config.displayName}
        onClose={() => navigate('/', { replace: true })}
        onUpgrade={() => undefined}
      />
    </div>
  );
}

function LevelRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPro } = useSubscription();
  const returnPath = lockedToolReturnPathFromSearch(location.search);

  if (isPro) {
    return <LevelScreen />;
  }

  return (
    <LockedProFeature
      title="Degree level"
      copy="Upgrade to unlock the zeroable degree level and the rest of Truebend Pro."
      feature="degree level"
      returnPath={returnPath}
      onClose={() => navigate(returnPath, { replace: true })}
    />
  );
}

function SquareAlignRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPro } = useSubscription();
  const returnPath = lockedToolReturnPathFromSearch(location.search);

  if (isPro) {
    return <LevelScreen toolMode="square-align" />;
  }

  return (
    <LockedProFeature
      title="Square Align"
      copy="Upgrade to align conduit square to a building reference and the rest of Truebend Pro."
      feature="square align"
      returnPath={returnPath}
      onClose={() => navigate(returnPath, { replace: true })}
    />
  );
}

function LockedProFeature({
  title,
  copy,
  feature,
  returnPath = '/',
  onClose,
}: {
  title: string;
  copy: string;
  feature: string;
  returnPath?: string;
  onClose: () => void;
}) {
  return (
    <div style={lockedRouteStyle}>
      <LockedBackLink returnPath={returnPath} />
      <section aria-label="Truebend Pro feature locked" style={lockedHeroStyle}>
        <div style={lockedEyebrowStyle}>Truebend Pro</div>
        <h1 style={lockedTitleStyle}>{title}</h1>
        <p style={lockedCopyStyle}>{copy}</p>
      </section>
      <PaywallSheet feature={feature} onClose={onClose} onUpgrade={() => undefined} />
    </div>
  );
}

function LockedBackLink({ returnPath }: { returnPath: string }) {
  return (
    <Link to={returnPath} aria-label={lockedReturnLabelForPath(returnPath)} style={lockedBackStyle}>
      <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 22, height: 22 }}>
        <path
          d="M15 5 8 12l7 7"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.4"
        />
      </svg>
      <span style={lockedBackTextStyle}>{lockedReturnShortLabelForPath(returnPath)}</span>
    </Link>
  );
}

function lockedToolReturnPathFromSearch(search: string): string {
  const value = new URLSearchParams(search).get('returnTo');
  if (
    !value ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('://') ||
    value.startsWith('/level') ||
    value.startsWith('/square-align') ||
    !isLockedReturnDestination(value)
  ) {
    return '/';
  }
  return value;
}

function isLockedReturnDestination(value: string): boolean {
  const pathOnly = value.split(/[?#]/)[0];
  if (pathOnly === '/' || pathOnly === '/settings') return true;
  return CALCULATORS.some((cfg) => pathOnly === `/${cfg.id}`);
}

function lockedReturnLabelForPath(returnPath: string): string {
  const pathOnly = returnPath.split(/[?#]/)[0];
  if (pathOnly === '/') return 'Back to all bends';
  if (pathOnly === '/settings') return 'Back to settings';
  return 'Back to calculator';
}

function lockedReturnShortLabelForPath(returnPath: string): string {
  const pathOnly = returnPath.split(/[?#]/)[0];
  if (pathOnly === '/') return 'Bends';
  if (pathOnly === '/settings') return 'Settings';
  return 'Calc';
}

const lockedRouteStyle: CSSProperties = {
  maxWidth: 430,
  minHeight: 'calc(100dvh - var(--space-8))',
  margin: '0 auto',
  display: 'grid',
  alignContent: 'start',
  gap: 'var(--space-4)',
};

const lockedBackStyle: CSSProperties = {
  height: 44,
  minWidth: 44,
  minHeight: 44,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  justifySelf: 'start',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-pill)',
  padding: '0 var(--space-3) 0 var(--space-2)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const lockedBackTextStyle: CSSProperties = {
  maxWidth: 68,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const lockedHeroStyle: CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
  padding: 'var(--space-5)',
};

const lockedEyebrowStyle: CSSProperties = {
  color: 'var(--color-blue)',
  fontSize: '0.68rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 'var(--space-1)',
};

const lockedTitleStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-3xl)',
  lineHeight: 1.04,
  marginBottom: 'var(--space-2)',
};

const lockedCopyStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  margin: 0,
  fontSize: 'var(--text-base)',
};

function SettingsNavLink() {
  return (
    <NavLink
      to="/settings"
      aria-label="Settings"
      title="Settings"
      style={({ isActive }) => ({
        width: 38,
        height: 38,
        minWidth: 38,
        display: 'grid',
        placeItems: 'center',
        color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)',
        background: isActive ? 'rgba(249, 115, 22, 0.1)' : 'transparent',
        border: `1px solid ${isActive ? 'var(--color-accent)' : 'transparent'}`,
        borderRadius: 'var(--radius-md)',
        textDecoration: 'none',
      })}
    >
      <GearIcon />
    </NavLink>
  );
}

function GearIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style={{ width: 20, height: 20, display: 'block' }}
    >
      <path
        d="M12 8.4a3.6 3.6 0 1 0 0 7.2 3.6 3.6 0 0 0 0-7.2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
      />
      <path
        d="M18.1 13.6c.07-.5.07-1.02 0-1.52l2-1.54-2-3.46-2.42.98a6.9 6.9 0 0 0-1.32-.76L14 4.72h-4l-.36 2.58c-.47.2-.91.45-1.32.76L5.9 7.08l-2 3.46 2 1.54c-.07.5-.07 1.02 0 1.52l-2 1.54 2 3.46 2.42-.98c.41.31.85.56 1.32.76l.36 2.58h4l.36-2.58c.47-.2.91-.45 1.32-.76l2.42.98 2-3.46-2-1.54Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.9"
      />
    </svg>
  );
}

function BackArrowIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style={{ width: 18, height: 18, display: 'block' }}
    >
      <path
        d="M15 5 8 12l7 7"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.4"
      />
    </svg>
  );
}

function TopNavLink({ to, label, exact }: { to: string; label: string; exact?: boolean }) {
  return (
    <NavLink
      to={to}
      end={exact ?? false}
      style={({ isActive }) => ({
        color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)',
        fontWeight: isActive ? 700 : 500,
        fontSize: 'var(--text-sm)',
        textDecoration: 'none',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: isActive ? 'rgba(249, 115, 22, 0.1)' : 'transparent',
        border: `1px solid ${isActive ? 'var(--color-accent)' : 'transparent'}`,
      })}
    >
      {label}
    </NavLink>
  );
}
